// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {SvgIconEl} from 'common/components/svg-icon';
import timing from 'common/stylesheets/mixins/timing.json';
import {TooltipEl} from 'common/components/tooltip';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {UserIconEl} from 'common/widgets/user-icon';
import {IconSizeType} from 'common/constants';
import {Placement} from 'common/components/tooltip/types';
import {SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {Directions} from 'common/components/drop-menu/types';
import {handleKeyPress} from 'common/util/focusable';
import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';

import {DropPosition} from '../constants';
import {NestingOrder, TagTheme} from '../types';
import type {SidenavItem} from './';

const TAG_THEME_MAP = {
  [TagTheme.Default]: TEXT_HIGHLIGHT_THEME.INFORMATION,
  [TagTheme.Neutral]: TEXT_HIGHLIGHT_THEME.GENERIC,
};

export function template(this: SidenavItem) {
  const dragHandlers = this.attr(`is-drag-disabled`)
    ? {}
    : {
        dragover: this.helpers.onDragOver,
        dragleave: this.helpers.onDragLeave,
        drop: this.helpers.onDrop,
        dragstart: this.helpers.onDragStart,
        dragend: this.helpers.onDragEnd,
      };

  const item = this.state.item;

  const isFirstInNestingOrder = this.attr(`nested-order`) === NestingOrder.First;
  const isLastInNestingOrder =
    (this.attr(`nested-order`) === NestingOrder.Last || this.attr(`nested-order`) === NestingOrder.First) &&
    (!this.attr(`is-expanded`) || !item.nestedItems || item.nestedItems.length === 0);

  return (
    <div
      sel='.draggable-wrapper'
      class={{
        'is-draggable': this.attr(`is-draggable`),
        'is-drag-disabled': this.attr(`is-drag-disabled`),
        'is-dragging': this.state.isDragging,
        'drop-indicator-above': this.state.dropPosition === DropPosition.Above,
        'drop-indicator-below': this.state.dropPosition === DropPosition.Below,
        'drop-indicator-on': this.state.dropPosition === DropPosition.On,
      }}
      attrs={{
        draggable: String(
          !this.attr(`is-drag-disabled`) && this.attr(`is-draggable`) && !this.state.isOverflowMenuOpen,
        ),
      }}
      on={dragHandlers}
    >
      <a
        sel='.item-wrapper'
        // setting draggable: `false` here prevents browser drag styling for anchor tags
        attrs={{elref: `sidenavItemLink`, tabindex: 0, href: item?.href, target: item?.target, draggable: `false`}}
        class={{
          'is-selected': this.attr(`is-selected`),
          'has-overflow-menu': this.helpers.hasOverflowMenu(),
          'no-top-border-radius': this.attr(`is-selected`) && !isFirstInNestingOrder,
          'no-bottom-border-radius': this.attr(`is-selected`) && !isLastInNestingOrder,
        }}
        on={{
          click: this.helpers.onClick,
          keypress: handleKeyPress,
        }}
      >
        {this.iconRenderer?.()}

        <div
          sel='.item'
          style={{
            width: `calc(100% - ${this.attr(`label-tab-size`) * 10 + (this.attr(`is-new`) ? 44 : 0)}px)`,
            marginLeft: `${this.attr(`label-tab-size`) * 10}px`,
          }}
          attrs={{
            draggable: `false`,
          }}
          class={{
            'is-new': this.attr(`is-new`),
          }}
        >
          {this.attr(`can-expand`) && (
            <ButtonEl
              sel='.caret-btn'
              attrs={{
                icon: this.attr(`is-expanded`) ? `caret-down` : `caret-right`,
                size: ButtonSize.Inline,
                theme: ButtonTheme.Tertiary,
              }}
              on={{
                click: (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  this.helpers.onCaretClick();
                },
              }}
            />
          )}
          <div sel='.tooltip-container'>
            {item.icon && (
              <div sel='.item-icon'>
                <SvgIconEl attrs={{icon: item.icon}} />
              </div>
            )}
            <div sel='.item-label'>{this.print(item.label)}</div>
            {item.iconRight && (
              <div sel='.item-right-icon'>
                <SvgIconEl attrs={{icon: item.iconRight, height: `18px`}} />
              </div>
            )}
            {item.tag && (
              <span sel='.item-tag-wrapper'>
                <TextHighlightEl
                  attrs={{
                    text: this.i18n.print(item.tag),
                    size: SIZE.SMALL,
                    theme: TAG_THEME_MAP[item.tagTheme || TagTheme.Default],
                  }}
                />
              </span>
            )}
            {!this.state.isOverflowMenuOpen && (
              <TooltipEl
                attrs={{
                  placement: Placement.Right,
                  'delay-in': timing[`$mp-slow-tooltip-delay-in`],
                  offset: 38,
                }}
              >
                <div sel='.tooltip'>
                  <div sel='.tooltip-label'>{item.tooltip ? this.print(item.tooltip) : this.print(item.label)}</div>

                  {item.userName && (
                    <div sel='.user-metadata'>
                      <UserIconEl
                        attrs={{
                          name: item.userName,
                          'disable-tooltip': true,
                          id: item.userId,
                          size: IconSizeType.Small,
                          email: item.userEmail,
                          'include-label': true,
                        }}
                      />
                    </div>
                  )}
                </div>
              </TooltipEl>
            )}
          </div>
          {this.helpers.hasOverflowMenu() && (
            <div
              sel='.overflow-menu-wrapper'
              class={{
                'is-selected': this.attr(`is-selected`),
              }}
              on={{
                click: (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                },
              }}
            >
              <SelectEl
                sel='.overflow-menu'
                class={{
                  'is-selected': this.attr(`is-selected`),
                }}
                attrs={{
                  theme: SelectTheme.Builder,
                  icon: {leftIcon: `hor-ellipsis`},
                  'icon-only': true,
                  'menu-width': 180,
                  'menu-height': 440,
                  'menu-alignment': Directions.Right,
                  'show-preview-tooltip': true,
                  'experimental-positioning': true,
                }}
                props={{
                  sections: item.overflowMenuSections,
                }}
                on={{
                  change: this.helpers.onOverflowMenuSelect,
                  menuChange: this.helpers.onOverflowMenuChange,
                }}
              />
            </div>
          )}
        </div>
        {this.attr(`is-new`) && (
          <TextHighlightEl
            sel='.new-badge'
            attrs={{
              text: `New`,
              theme: TEXT_HIGHLIGHT_THEME.INFORMATION,
            }}
          />
        )}
      </a>
    </div>
  );
}
